import { useMemo } from 'react';
import { useAuthenticatedUser } from 'src/components/Auth/hooks/useAuthenticatedUser';
import { useReadUserArticleRelationsByArticleIds } from 'src/components/HomepageRecommendedPosts/hooks/useReadUserArticleRelationsByArticleIds';
import { Article, UserArticleRelation } from 't2-graphql-types';
import { PROMPT_REPLIES_PAGE_SIZE } from '../constants';
import { usePromptArticlesQuery } from './usePromptArticlesQuery';

export type TPromptArticlesQuery = ReturnType<typeof usePromptArticles>;

export const usePromptArticles = ({
  promptId,
  skip = 0,
  take = PROMPT_REPLIES_PAGE_SIZE,
}: {
  promptId: string;
  skip?: number;
  take?: number;
}) => {
  const { authenticatedUserId: userId } = useAuthenticatedUser();
  const promptArticleQuery = usePromptArticlesQuery({ promptId, skip, take });
  const userArtcleRelationsQuery = useReadUserArticleRelationsByArticleIds({
    variables: {
      userId: userId || '',
      articleIds: promptArticleQuery.data?.article?.childArticles?.map((a) => a.id) || [],
    },
  });

  const prompt = useMemo(() => promptArticleQuery.data?.article, [promptArticleQuery.data?.article]);
  const promptChildArticles = useMemo(() => prompt?.childArticles, [prompt]);
  const promptWithUserRelations = useMemo<Article | null>(() => {
    if (!prompt) {
      return null;
    }

    return {
      ...(prompt || {}),
      childArticles: (promptChildArticles || []).map((promptChild) => ({
        ...(promptChild as Article),
        userRelations: userArtcleRelationsQuery.data?.userArticleRelations
          .filter((userArticleRelation) => userArticleRelation.article.id === promptChild.id)
          .filter(Boolean) as UserArticleRelation[],
      })),
    };
  }, [prompt, userArtcleRelationsQuery.data]);

  return { ...promptArticleQuery, data: promptWithUserRelations ? { article: promptWithUserRelations } : null };
};
